import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  ViewEncapsulation
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Params, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { NovoChamadoFormProvider } from 'src/app/providers/novoChamadoFormProvider';
import {
  IContrato,
  IDefeito,
  IEquipamento,
  IGetContratosQueryParams,
  ISite
} from 'src/app/interfaces';
import { ListaPaginada } from 'src/app/models';
import {
  AuthenticationService,
  ContratoService,
  DefeitoService,
  SiteService,
  EquipamentoService
} from 'src/app/services';
import { ENVIRONMENT } from 'src/app/constants';
import { Environment } from 'src/app/types';

@Component({
  selector: 'app-abrir-chamado-a',
  templateUrl: './abrir-chamado-a.component.html',
  styleUrls: ['./abrir-chamado-a.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AbrirChamadoAComponent implements OnInit, OnDestroy {
  subscriptions$ = new Subscription();

  contratos = new ListaPaginada<IContrato>();
  defeitos: IDefeito[];
  equipamentos = new ListaPaginada<IEquipamento>();
  sites = new ListaPaginada<ISite>();

  form: FormGroup;
  params: Params;

  token = this.authenticationService.obterToken();
  qrCodeToken: string | null = null;
  processandoQrCode = false;

  constructor(
    private novoChamadoFormProvider: NovoChamadoFormProvider,
    private defeitoService: DefeitoService,
    private contratoService: ContratoService,
    private equipamentoService: EquipamentoService,
    private authenticationService: AuthenticationService,
    private siteService: SiteService,
    private router: Router,
    @Inject(ENVIRONMENT) public environment: Environment
  ) {}

  //#region Controle input contrato
  filtrarContratos(
    valor: IContrato | string,
    dados: IContrato[],
    exibir: this['exibirContrato']
  ): IContrato[] {
    return dados.filter((v) => {
      if (typeof valor === 'string') {
        return exibir(v).toLowerCase().includes(valor.toLowerCase());
      }
      return v.codContrato === valor?.codContrato;
    });
  }

  exibirContrato(valor: IContrato) {
    if (valor !== null && Object.keys(valor).length > 0) {
      return `${valor?.codContrato} - ${
        valor?.nomeFantasiaCliente || valor?.nomeCliente || ''
      }`;
    }
    return '';
  }

  valorContrato(valor: IContrato) {
    return valor;
  }

  comparaContrato(valor1: string | IContrato | null, valor2: IContrato) {
    if (!valor1 || typeof valor1 === 'string') return false;
    if (valor1?.codContrato !== valor2?.codContrato) return false;
    return true;
  }
  //#endregion

  //#region Controle input site
  filtrarSites(
    valor: ISite | string,
    dados: ISite[],
    exibir: this['exibirSite']
  ): ISite[] {
    return dados.filter((v) => {
      if (typeof valor === 'string') {
        return exibir(v).toLowerCase().includes(valor.toLowerCase());
      }
      return v.codSite === valor?.codSite;
    });
  }

  exibirSite(valor: ISite) {
    if (valor !== null && Object.keys(valor).length > 0) {
      return `${valor?.nome} - ${valor?.endereco}`;
    }
    return '';
  }

  valorSite(valor: ISite) {
    return valor;
  }

  comparaSite(valor1: string | ISite | null, valor2: ISite) {
    if (!valor1 || typeof valor1 === 'string') return false;
    if (valor1?.codSite !== valor2?.codSite) return false;
    return true;
  }
  //#endregion

  //#region Controle input equipamento
  filtrarEquipamentos(
    valor: IEquipamento | string,
    dados: IEquipamento[],
    exibir: this['exibirEquipamento']
  ): IEquipamento[] {
    return dados.filter((v) => {
      if (typeof valor === 'string') {
        return exibir(v).toLowerCase().includes(valor.toLowerCase());
      }
      return v.numSerie === valor?.numSerie;
    });
  }

  exibirEquipamento(valor: IEquipamento) {
    if (valor !== null && Object.keys(valor).length > 0) {
      return `${valor?.numSerie} | ${valor?.modelo} - ${
        valor?.local || 'Sem dados'
      }`;
    }
    return '';
  }

  valorEquipamento(valor: IEquipamento) {
    return valor;
  }

  comparaEquipamento(
    valor1: string | IEquipamento | null,
    valor2: IEquipamento
  ) {
    if (!valor1 || typeof valor1 === 'string') return false;
    if (valor1?.numSerie !== valor2?.numSerie) return false;
    return true;
  }
  //#endregion

  getDefeitos() {
    this.subscriptions$.add(
      this.defeitoService.getDefeitos().subscribe({
        next: ({ data }) => (this.defeitos = data)
      })
    );
  }

  getContratos() {
    const queryParams: IGetContratosQueryParams = {
      idUsuario: this.token.id,
      codContrato: this.token.codContrato,
      codGrupoEconomico: this.token.codGrupoEconomico,
      contratoAtivo: true,
      paginar: false
    };

    this.subscriptions$.add(
      this.contratoService.getContratos(queryParams).subscribe({
        next: ({ data }) => {
          this.contratos = data;

          // Após carregar contratos, verifica se há QR Code token
          this.verificarQrCodeToken();
        }
      })
    );
  }

  getSite(codContrato?: string) {
    const contratoValue = this.form.get('contrato')?.value;
    const codContratoToUse = contratoValue?.codContrato || codContrato;

    if (!codContratoToUse) {
      this.sites = new ListaPaginada<ISite>();
      return;
    }

    this.subscriptions$.add(
      this.siteService
        .getSite({
          codContrato: [codContratoToUse],
          codGrupoEconomico: this.token.codGrupoEconomico,
          tipoUsuario: this.token.tipoUsuario,
          paginar: false
        })
        .subscribe({
          next: ({ data }) => {
            this.sites = data;

            // Após carregar sites, tenta selecionar o site do QR Code
            if (this.processandoQrCode && this.qrCodeData) {
              this.selecionarSitePorQrCode();
            }
          }
        })
    );
  }

  getEquipamentos(codSite?: string) {
    const contratoValue = this.form.get('contrato')?.value;
    const siteValue = this.form.get('site')?.value;

    if (!contratoValue?.codContrato) {
      this.equipamentos = new ListaPaginada<IEquipamento>();
      return;
    }

    this.subscriptions$.add(
      this.equipamentoService
        .getEquipamentos({
          codContrato: [contratoValue.codContrato],
          codSite: siteValue?.codSite || codSite,
          idUsuario: this.token.id,
          CodGrupoEconomico: this.token.codGrupoEconomico,
          TipoUsuario: this.token.tipoUsuario,
          contratoAtivo: true,
          equipamentoAtivo: true,
          paginar: false,
          NomeSite: this.token.nome
        })
        .subscribe({
          next: ({ data }) => {
            this.equipamentos.registros = data.registros;

            // Após carregar equipamentos, tenta selecionar o equipamento do QR Code
            if (this.processandoQrCode && this.qrCodeData) {
              this.selecionarEquipamentoPorQrCode();
            }
          }
        })
    );
  }

  // Dados temporários do QR Code
  private qrCodeData: {
    codContrato: string;
    codSite: string;
    numSerie: string;
  } | null = null;

  verificarQrCodeToken() {
    this.qrCodeToken = sessionStorage.getItem('qrCodeToken');

    if (this.qrCodeToken) {
      this.validarQrCodeToken(this.qrCodeToken);
    }
  }

  validarQrCodeToken(token: string) {
    this.processandoQrCode = true;

    this.subscriptions$.add(
      this.equipamentoService.validarTokenEquipamento(token).subscribe({
        next: ({ data }) => {
          if (!data?.tokenValido || !data?.equipamentoEncontrado) {
            this.processandoQrCode = false;
            sessionStorage.removeItem('qrCodeToken');
            return;
          }

          // Guarda os dados do QR Code
          this.qrCodeData = {
            codContrato: data.contrato.tB02111_CODIGO,
            codSite: data.site.tB02176_CODIGO,
            numSerie: data.equipamento.tB02112_NUMSERIE
          };

          // Tenta selecionar o contrato na lista já carregada
          this.selecionarContratoPorQrCode();
        },
        error: () => {
          this.processandoQrCode = false;
          sessionStorage.removeItem('qrCodeToken');
        }
      })
    );
  }

  selecionarContratoPorQrCode() {
    if (!this.qrCodeData) return;

    const contratoEncontrado = this.contratos.registros?.find(
      (contrato) => contrato.codContrato === this.qrCodeData?.codContrato
    );

    if (contratoEncontrado) {
      // Seleciona o contrato no formulário (sem emitir eventos para evitar loops)
      this.form.patchValue(
        { contrato: contratoEncontrado },
        { emitEvent: false }
      );

      // Carrega os sites para este contrato
      this.getSite(this.qrCodeData.codContrato);
    } else {
      console.warn('Contrato do QR Code não encontrado na lista');
      this.processandoQrCode = false;
      sessionStorage.removeItem('qrCodeToken');
    }
  }

  selecionarSitePorQrCode() {
    if (!this.qrCodeData) return;

    const siteEncontrado = this.sites.registros?.find(
      (site) => site.codSite === this.qrCodeData?.codSite
    );

    if (siteEncontrado) {
      // Seleciona o site no formulário
      this.form.patchValue({ site: siteEncontrado }, { emitEvent: false });

      // Carrega os equipamentos para este site
      this.getEquipamentos(this.qrCodeData.codSite);
    } else {
      console.warn('Site do QR Code não encontrado na lista');
      this.processandoQrCode = false;
    }
  }

  selecionarEquipamentoPorQrCode() {
    if (!this.qrCodeData) return;

    const equipamentoEncontrado = this.equipamentos.registros?.find(
      (equipamento) => equipamento.numSerie === this.qrCodeData?.numSerie
    );

    if (equipamentoEncontrado) {
      // Seleciona o equipamento no formulário
      this.form.patchValue(
        { equipamento: equipamentoEncontrado },
        { emitEvent: false }
      );

      // Finaliza o processamento do QR Code
      this.processandoQrCode = false;
      sessionStorage.removeItem('qrCodeToken');
      this.qrCodeData = null;
    } else {
      console.warn('Equipamento do QR Code não encontrado na lista');
      this.processandoQrCode = false;
    }
  }

  prosseguir() {
    this.router.navigate(['equipamento/abrir-chamado/2']);
  }

  ngOnInit(): void {
    this.form = this.novoChamadoFormProvider
      .getForm()
      .get('primeiroPasso') as FormGroup;

    // Sempre carrega defeitos
    this.getDefeitos();

    // Carrega contratos (dentro do getContratos verificará o QR Code)
    this.getContratos();

    // Configura subscriptions para valueChanges
    this.configurarSubscriptions();

    // Carrega dados iniciais se já existirem no formulário
    if (this.form.get('contrato')?.value?.codContrato) {
      this.getSite();
    }
    if (this.form.get('site')?.value?.codSite) {
      this.getEquipamentos();
    }
  }

  configurarSubscriptions() {
    // Subscription para mudanças no contrato
    this.subscriptions$.add(
      this.form.get('contrato')?.valueChanges.subscribe((v) => {
        // Se estiver processando QR Code, ignora mudanças
        if (this.processandoQrCode) return;

        if (v && typeof v !== 'string') {
          this.form.patchValue(
            { site: null, equipamento: null },
            { emitEvent: false }
          );
          this.sites = new ListaPaginada<ISite>();
          this.equipamentos = new ListaPaginada<IEquipamento>();
          this.getSite(v.codContrato);
          return;
        }

        if (v === null || v === '') {
          this.form.patchValue(
            {
              site: null,
              equipamento: null
            },
            { emitEvent: false }
          );
          this.sites = new ListaPaginada<ISite>();
          this.equipamentos = new ListaPaginada<IEquipamento>();
        }
      })
    );

    // Subscription para mudanças no site
    this.subscriptions$.add(
      this.form.get('site')?.valueChanges.subscribe((v) => {
        // Se estiver processando QR Code, ignora mudanças
        if (this.processandoQrCode) return;

        if (v && typeof v !== 'string') {
          this.form.patchValue({ equipamento: null }, { emitEvent: false });
          this.equipamentos = new ListaPaginada<IEquipamento>();
          this.getEquipamentos(v.codSite);
          return;
        }

        if (v === null || v === '') {
          this.form.patchValue({ equipamento: null }, { emitEvent: false });
          this.equipamentos = new ListaPaginada<IEquipamento>();
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions$.unsubscribe();
  }
}
